/**
 * Use free gift module to display free gift associated with a SKU
 *
 * @module views/components/AutomatedFreeGift
 * @memberof -Common
 */
import React from 'react';

import PropTypes from 'prop-types';

import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import FreeGift from '../FreeGift/FreeGift';

/**
 * Represents a AutomatedFreeGift component
 *
 * @method
 * @param {AutomatedFreeGiftProps} props - React properties passed from composition
 * @returns AutomatedFreeGift
 */
export const AutomatedFreeGift = function( props ){
  const {
    setShowSpacer
  } = props;

  if( !hasItems( props?.gifts ) ){
    setShowSpacer( false )
    return null;
  }

  return (
    <div className='AutomatedFreeGift'>
      { props && props?.gifts?.length > 0 &&
        <FreeGift
          { ...spreadProps( { ...defaultProps } ) }
          { ...spreadProps( { ...props } ) }
        />
      }
    </div>
  );
};

/**
  * Property type definitions
  * @typedef AutomatedFreeGiftProps
  * @type {object}
  * @property {array} gifts - The default value for gifts is set to empty array.
  * @property {object} title - That contains the title and other properties.
  * @property {string} backgroundColor - Sets the title for productRail list.
  * @property {object} backgroundImage - The prop that holds background image.
  * @property {string} productId - This is the productId
  * @property {string} skuId - This is the skuId of the product
  * @property {string} carouselDetailsAccessibility - Value for aria label for carousel.
  * @property {string} carouselAccessibility - Value for aria label for carousel.
  * @property {string} carouselCardAccessibility - Value for the product card aria label.
  * @property {string} previousAccessibility - Sets the aria label for previous button.
  * @property {string} nextAccessibility - Sets the aria label for next button.
  * @property {object} previousClickAction - This is the action object for the Next button.
  * @property {object} nextClickAction - This is the action object for the Previous button.
  * @property {function} setShowSpacer - Sets the spacer below the component
  */
export const propTypes = {
  /** The prop that holds backgroundColor for the carousel.*/
  backgroundColor: PropTypes.string,
  /** The prop that holds title for the carousel.*/
  title: PropTypes.object,
  /** The props that the array of gifts */
  gifts: PropTypes.array,
  /** The prop that holds background image */
  backgroundImage: PropTypes.shape( {
    src: PropTypes.string,
    alt: PropTypes.string,
    metaData: PropTypes.shape( {
      width: PropTypes.number,
      height: PropTypes.number
    } )
  } ),
  /** This is the product id */
  productId: PropTypes.string,
  /** This is the sku Id for the product */
  skuId: PropTypes.string,
  /** The prop that holds the aria label for previous button. */
  previousAccessibility: PropTypes.string,
  /** The prop that holds the aria label for next button. */
  nextAccessibility: PropTypes.string,
  /** The prop that holds the action for the next button. */
  nextClickAction: PropTypes.object,
  /** The prop that holds the action for the previous button. */
  previousClickAction: PropTypes.object,
  /** The prop that holds aria label for carousel. */
  carouselDetailsAccessibility: PropTypes.string,
  /** The prop that holds aria label for carousel. */
  carouselAccessibility: PropTypes.string,
  /** The prop that holds aria label for card. */
  carouselCardAccessibility: PropTypes.string,
  /** Sets the spacer below the component */
  setShowSpacer: PropTypes.func
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} backgroundColor='magenta-100' - The default value of the backgroundColor is magenta-100.
 */
export const defaultProps =  {
  backgroundColor: 'magenta-100'
};

AutomatedFreeGift.propTypes = propTypes;
AutomatedFreeGift.defaultProps = defaultProps;

export default AutomatedFreeGift;
